// src/helpers/loisir.js
import { deleteData, getData, postData, putData } from './ApiSettings';

const LOISIR_SESSION_KEY = 'ovlm_loisir_selection';

export const LoisirManager = {
  // Save the current loisir selection to session storage
  saveSelection: (selection) => {
    sessionStorage.setItem(LOISIR_SESSION_KEY, JSON.stringify(selection));
  },

  // Get the current loisir selection from session storage
  getSelection: () => {
    const selection = sessionStorage.getItem(LOISIR_SESSION_KEY);
    return selection ? JSON.parse(selection) : null;
  },

  // Clear the loisir selection from session storage
  clearSelection: () => {
    sessionStorage.removeItem(LOISIR_SESSION_KEY);
  },

  // Fetch all available formulas
  fetchFormulas: async () => {
    try {
      const response = await getData('/formulas?populate=*');
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching formulas:', error);
      throw error;
    }
  },

  // Fetch a specific formula by ID
  fetchFormulaById: async (formulaId) => {
    try {
      const response = await getData(`/formulas/${formulaId}?populate=*`);
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching formula with ID ${formulaId}:`, error);
      throw error;
    }
  },

  // Fetch all loisir products with availability check
  fetchLoisirProducts: async (categoryId = null, startDate = null, endDate = null) => {
    try {
      let endpoint = '/loisir-products?populate=*&filters[available][$eq]=true';
      
      if (categoryId) {
        endpoint += `&filters[category][id][$eq]=${categoryId}`;
      }

      const response = await getData(endpoint);
      let products = response.data.data || [];

      // If dates are provided, check availability for those dates
      if (startDate && endDate) {
        products = await Promise.all(
          products.map(async (product) => {
            const availableQuantity = await LoisirManager.getAvailableQuantity(
              product.id, 
              startDate, 
              endDate
            );
            return {
              ...product,
              availableQuantity,
              isAvailable: availableQuantity > 0
            };
          })
        );
        
        // Filter out products with no availability
        products = products.filter(product => product.isAvailable);
      }

      return products;
    } catch (error) {
      console.error('Error fetching loisir products:', error);
      throw error;
    }
  },

  // Get available quantity for a product during a specific period
  getAvailableQuantity: async (productId, startDate, endDate) => {
    try {
      // First get the product's total quantity
      const productResponse = await getData(`/loisir-products?filters[id][$eq]=${productId}`);
      // console.log(productResponse)
      const totalQuantity = productResponse.data.data[0].quantity || 1;

      // Get all active orders that overlap with the requested period
      const ordersResponse = await getData(`/formula-orders?populate=*&filters[$and][0][selected_products][id][$eq]=${productId}&filters[$and][1][order_status][$in][0]=paid&filters[$and][1][order_status][$in][1]=delivered&filters[$and][2][$or][0][$and][0][start_date][$lte]=${endDate}&filters[$and][2][$or][0][$and][1][end_date][$gte]=${startDate}`);
      
      const activeOrders = ordersResponse.data.data || [];
      
      // Calculate total reserved quantity during this period
      let reservedQuantity = 0;
      activeOrders.forEach(order => {
        const productDetails = order.selected_products_details;
        if (productDetails && productDetails[productId]) {
          reservedQuantity += productDetails[productId].quantity || 1;
        } else {
          // Fallback: if product is in the order but no details, assume quantity 1
          if (order.selected_products.some(p => p.id === productId)) {
            reservedQuantity += 1;
          }
        }
      });

      return Math.max(0, totalQuantity - reservedQuantity);
    } catch (error) {
      console.error('Error calculating available quantity:', error);
      return 0;
    }
  },

  // Check if products are available in requested quantities
  checkProductsAvailability: async (productSelections, startDate, endDate) => {
    try {
      const availabilityResults = await Promise.all(
        productSelections.map(async (selection) => {
          const availableQuantity = await LoisirManager.getAvailableQuantity(
            selection.productId,
            startDate,
            endDate
          );
          return {
            productId: selection.productId,
            requestedQuantity: selection.quantity,
            availableQuantity,
            isAvailable: availableQuantity >= selection.quantity
          };
        })
      );

      return availabilityResults;
    } catch (error) {
      console.error('Error checking products availability:', error);
      throw error;
    }
  },

  // Get a formula order by document ID
  getFormulaOrderByDocumentId: async (documentId) => {
    try {
      const response = await getData(`/formula-orders?filters[documentId][$eq]=${documentId}&populate=*`);
      if (response.data?.data && response.data.data.length > 0) {
        return response.data.data[0];
      }
      throw new Error('Formula order not found');
    } catch (error) {
      console.error('Error fetching formula order:', error);
      throw error;
    }
  },

  // Create a loisir order
  createLoisirOrder: async (orderData) => {
    try {
      // Prepare selected products details with quantities
      const selectedProductsDetails = {};
      orderData.selectedProducts.forEach(productSelection => {
        selectedProductsDetails[productSelection.productId] = {
          quantity: productSelection.quantity || 1
        };
      });

      const response = await postData('/formula-orders', {
        data: {
          user: orderData.userId,
          formula: orderData.formulaId,
          duration_days: orderData.durationDays,
          selected_products: orderData.selectedProducts.map(ps => ps.productId),
          selected_products_details: selectedProductsDetails,
          start_date: orderData.startDate,
          end_date: orderData.endDate,
          total_price: orderData.totalPrice,
          deposit_amount: orderData.depositAmount,
          order_status: 'pending_payment',
          delivery_method: orderData.deliveryMethod,
          delivery_price: orderData.deliveryPrice,
          comments: orderData.comments,
          user_details: orderData.userDetails,
          relay_point: orderData.relayPoint
        }
      });

      return {
        success: true,
        orderId: response.data.data.id,
        documentId: response.data.data.documentId
      };
    } catch (error) {
      console.error('Error creating loisir order:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Update a formula order
  updateFormulaOrder: async (orderId, data) => {
    try {
      const response = await putData(`/formula-orders/${orderId}`, {
        data
      });
      return response.data.data;
    } catch (error) {
      console.error('Error updating formula order:', error);
      return null;
    }
  },

  // Calculate price for selected formula and duration
  calculatePrice: (formula, durationDays) => {
    if (!formula || !formula.components_pricing_formula_prices) return { price: 0, deposit: 0 };

    const priceEntry = formula.components_pricing_formula_prices.find(p => p.duration_days === durationDays);
    if (!priceEntry) return { price: 0, deposit: 0 };

    return {
      price: priceEntry.price,
      deposit: priceEntry.deposit
    };
  },

  // Format product selection with quantities for display
  formatProductSelection: (productSelections) => {
    return productSelections.map(selection => ({
      ...selection,
      displayText: selection.quantity > 1 
        ? `${selection.title} (x${selection.quantity})`
        : selection.title
    }));
  }
};

export default LoisirManager;